// pages/expenses/ExpenseList.jsx
import React, { useState, useEffect, useCallback, useMemo, } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Table from '../../core/pagination/datatable';
import {
    ChevronUp,

    Sliders,
    Filter,
    Search,
    Eye,
    Edit,
    Trash2,
    PlusCircle,
    DollarSign,
    TrendingUp,

    Home,
    Briefcase,
    Circle,
    FileText,
    Folder,
    Zap,
    Users,
    MapPin,
    Tool,
    RefreshCw
} from 'feather-icons-react/build/IconComponents';
import { expenseActions } from '../../core/action/expenseActions';
import AddExpense from '../../core/modals/expenses/AddExpense';
import EditExpense from '../../core/modals/expenses/EditExpense';
import ViewExpense from '../../core/modals/expenses/ViewExpense';

const MySwal = withReactContent(Swal);

const ExpenseList = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);


    // State
    const [expenses, setExpenses] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true); // Start with true
    const [initialLoad, setInitialLoad] = useState(true);
    const [isFilterVisible, setIsFilterVisible] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [summary, setSummary] = useState({
        total_expenses: 0,
        total_amount: 0,
        average_amount: 0,
        total_categories: 0,
        total_payment_methods: 0
    });
    const [categorySummary, setCategorySummary] = useState([]);

    // Modal states
    const [modalType, setModalType] = useState(null);
    const [selectedExpense, setSelectedExpense] = useState(null);

    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });

    // Filters
    const [filters, setFilters] = useState({
        search: '',
        category_id: '',
        payment_method: '',
        status: '',
        from_date: null,
        to_date: null
    });

    // Sort options
    const sortOptions = useMemo(() => [
        { value: 'created_at', label: 'Newest First' },
        { value: 'expense_date', label: 'Date' },
        { value: 'expense_no', label: 'Expense Number' },
        { value: 'category', label: 'Category' },
        { value: 'amount', label: 'Amount' },
        { value: 'payment_method', label: 'Payment Method' },
        { value: 'status', label: 'Status' }
    ], []);

    const [sortOption, setSortOption] = useState('created_at');

    // Payment method options
    const paymentMethodOptions = useMemo(() => [
        { value: '', label: 'All Methods' },
        { value: 'cash', label: 'Cash' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'cheque', label: 'Cheque' },
        { value: 'credit_card', label: 'Credit Card' },
        { value: 'upi', label: 'UPI' }
    ], []);

    // Status options
    const statusOptions = useMemo(() => [
        { value: '', label: 'All Status' },
        { value: 'paid', label: 'Paid' },
        { value: 'unpaid', label: 'Unpaid' },
        { value: 'pending', label: 'Pending' }
    ], []);



    // Load initial data
    useEffect(() => {
        const loadInitialData = async () => {
            await loadCategories();
            await loadExpenses(1);
        };

        loadInitialData();
    }, []); // Empty dependency array - runs once

    // Load categories
    const loadCategories = async () => {
        try {
            const result = await expenseActions.getCategories();
            if (result.success && result.data) {
                setCategories(result.data || []);
                console.log('Categories loaded:', result.data.length);
            }
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    // Load expenses
    const loadExpenses = useCallback(async (page = 1, overrideFilters = null, overrideSortOption = null) => {
        try {
            setLoading(true);
            setApiError(null);

            const effectiveFilters = overrideFilters !== null ? overrideFilters : filters;
            const effectiveSortOption = overrideSortOption !== null ? overrideSortOption : sortOption;

            const params = {
                page,
                limit: pagination.limit,
                sort_by: effectiveSortOption
            };

            // Add filters only if they have values
            if (effectiveFilters.search) params.search = effectiveFilters.search;
            if (effectiveFilters.category_id) params.category_id = effectiveFilters.category_id;
            if (effectiveFilters.payment_method) params.payment_method = effectiveFilters.payment_method;
            if (effectiveFilters.status) params.status = effectiveFilters.status;
            if (effectiveFilters.from_date) {
                params.from_date = effectiveFilters.from_date instanceof Date
                    ? effectiveFilters.from_date.toISOString().split('T')[0]
                    : effectiveFilters.from_date;
            }
            if (effectiveFilters.to_date) {
                params.to_date = effectiveFilters.to_date instanceof Date
                    ? effectiveFilters.to_date.toISOString().split('T')[0]
                    : effectiveFilters.to_date;
            }

            console.log('Loading expenses with params:', params);

            const result = await expenseActions.getExpenses(params);

            console.log('Expenses API response:', result);

            // In loadExpenses function, after getting result:
            if (result.success) {
                setExpenses(result.data || []);
                setPagination({
                    page: result.page || page,
                    limit: pagination.limit,
                    total: result.total || 0,
                    totalPages: result.totalPages || 1
                });

                setSummary({
                    total_expenses: result.summary?.total_expenses || 0,
                    total_amount: result.summary?.total_amount || 0,
                    average_amount: result.summary?.average_amount || 0,
                    total_categories: result.summary?.total_categories || 0,
                    total_payment_methods: result.summary?.total_payment_methods || 0
                });

                setCategorySummary(result.categorySummary || []);

                console.log('Expenses loaded:', result.data.length, 'items');
                console.log('Summary:', result.summary);
                console.log('Category Summary:', result.categorySummary);
            } else {
                setApiError(result.message || 'Failed to load expenses');
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: result.message || 'Failed to load expenses',
                    timer: 3000,
                    showConfirmButton: false
                });
            }

        } catch (error) {
            console.error('Error loading expenses:', error);

            setApiError(error.message || 'Failed to load expenses');
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load expenses. Please try again.',
                timer: 3000,
                showConfirmButton: false
            });

        } finally {

            setLoading(false);
            setInitialLoad(false);

        }
    }, [pagination.limit, sortOption, filters]);

    // Handle search
    const handleSearch = useCallback(() => {
        setPagination(prev => ({ ...prev, page: 1 }));
        loadExpenses(1);
    }, [loadExpenses]);

    // Toggle filter visibility
    const toggleFilterVisibility = useCallback(() => {
        setIsFilterVisible((prev) => !prev);
    }, []);

    // Reset filters
    const resetFilters = useCallback(() => {
        const emptyFilters = {
            search: '',
            category_id: '',
            payment_method: '',
            status: '',
            from_date: null,
            to_date: null
        };
        setFilters(emptyFilters);
        setSortOption('expense_date');
        setPagination(prev => ({ ...prev, page: 1 }));
        loadExpenses(1, emptyFilters, 'expense_date');
    }, [loadExpenses]);

    // Handle filter change
    const handleFilterChange = useCallback((filterName, value) => {
        setFilters(prev => ({ ...prev, [filterName]: value }));
    }, []);

    // Handle sort change
    const handleSortChange = useCallback((option) => {
        setSortOption(option?.value || 'expense_date');
        setPagination(prev => ({ ...prev, page: 1 }));
        setTimeout(() => loadExpenses(1), 0);
    }, [loadExpenses]);

    // Handle page change
    const handlePageChange = useCallback((page) => {
        setPagination(prev => ({ ...prev, page }));
        loadExpenses(page);
    }, [loadExpenses]);

    // Modal handlers
    const handleAdd = useCallback(() => {
        setModalType('add');
        setSelectedExpense(null);
    }, []);

    const handleView = useCallback((expense) => {
        setSelectedExpense(expense);
        setModalType('view');
    }, []);

    const handleEdit = useCallback((expense) => {
        setSelectedExpense(expense);
        setModalType('edit');
    }, []);

    const handleModalClose = useCallback(() => {
        setModalType(null);
        setSelectedExpense(null);
    }, []);

    const handleSuccess = useCallback(() => {
        loadExpenses(pagination.page);
        loadCategories();
        handleModalClose();
    }, [loadExpenses, pagination.page, handleModalClose]);

    // Handle delete
    const handleDelete = useCallback(async (expense) => {
        const result = await MySwal.fire({
            title: 'Delete Expense?',
            html: `
                <div class="text-start">
                    <p>Are you sure you want to delete this expense?</p>
                    <p><strong>Expense #:</strong> ${expense.expense_no || 'N/A'}</p>
                    <p><strong>Date:</strong> ${expenseActions.formatDate(expense.expense_date)}</p>
                    <p><strong>Amount:</strong> ${expenseActions.formatCurrency(expense.amount || 0)}</p>
                    <p><strong>Paid To:</strong> ${expense.paid_to || 'N/A'}</p>
                    <p class="text-danger">This action cannot be undone.</p>
                </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const deleteResult = await expenseActions.deleteExpense(expense.id);
                if (deleteResult.success) {
                    MySwal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Expense has been deleted.',
                        timer: 1500,
                        showConfirmButton: false
                    });
                    loadExpenses(pagination.page);
                } else {
                    MySwal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: deleteResult.message || 'Failed to delete expense',
                        timer: 2000,
                        showConfirmButton: false
                    });
                }

            } catch (error) {
                console.error('Error deleting expense:', error);

                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to delete expense',
                    timer: 2000,
                    showConfirmButton: false
                });

            } finally {

                setLoading(false);

            }
        }
    }, [loadExpenses, pagination.page]);

    // Get category icon component
    const getCategoryIcon = useCallback((iconName) => {
        const icons = {
            'Briefcase': <Briefcase size={16} />,
            'Home': <Home size={16} />,
            'Zap': <Zap size={16} />,
            'Users': <Users size={16} />,
            'MapPin': <MapPin size={16} />,
            'TrendingUp': <TrendingUp size={16} />,
            'Tool': <Tool size={16} />,
            'Folder': <Folder size={16} />
        };
        return icons[iconName] || <Circle size={16} />;
    }, []);

    // Category options for filter dropdown
    const categoryOptions = useMemo(() => {
        return [
            { value: '', label: 'All Categories' },
            ...(categories || []).map(cat => ({
                value: cat.id,
                label: cat.category_name
            }))
        ];
    }, [categories]);

    // Table columns
    const columns = useMemo(() => [
        {
            title: "Expense No",
            dataIndex: "expense_no",
            sorter: true,
            render: (text, record) => (
                <div>
                    <strong>{text || 'N/A'}</strong>
                    <br />
                    <small className="text-muted">{expenseActions.formatDate(record?.expense_date)}</small>
                </div>
            ),
        },
        {
            title: "Category",
            dataIndex: "category_name",
            render: (text, record) => (
                <div className="d-flex align-items-center">
                    <span
                        className="badge me-2"
                        style={{
                            backgroundColor: record?.category_color || '#6c757d',
                            width: '20px',
                            height: '20px',
                            borderRadius: '4px'
                        }}
                    >
                        {record?.category_icon && getCategoryIcon(record.category_icon)}
                    </span>
                    <span>{text || 'Uncategorized'}</span>
                </div>
            ),
        },
        {
            title: "Amount",
            dataIndex: "amount",
            sorter: true,
            render: (text) => (
                <strong className="text-danger">
                    {expenseActions.formatCurrency(text || 0)}
                </strong>
            ),
        },
        {
            title: "Payment Method",
            dataIndex: "payment_method",
            render: (text) => (
                <div>
                    <span className={`badge ${expenseActions.getPaymentMethodBadge(text)}`}>
                        {expenseActions.getPaymentMethodIcon(text)} {text?.replace('_', ' ') || 'N/A'}
                    </span>
                </div>
            ),
        },
        {
            title: "Paid To",
            dataIndex: "paid_to",
            render: (text) => text || 'N/A',
        },
        {
            title: "Description",
            dataIndex: "description",
            render: (text) => (
                <div>
                    <span className="text-truncate d-inline-block" style={{ maxWidth: '200px' }}>
                        {text || 'N/A'}
                    </span>
                </div>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            render: (text) => (
                <span className={`badge ${expenseActions.getStatusBadge(text)}`}>
                    {text?.toUpperCase() || 'N/A'}
                </span>
            ),
        },
        {
            title: "Actions",
            dataIndex: "actions",
            render: (text, record) => (
                <div className="btn-group btn-group-sm" role="group">
                    <OverlayTrigger placement="top" overlay={<Tooltip>View Details</Tooltip>}>
                        <button
                            className="btn btn-outline-info"
                            onClick={() => handleView(record)}
                        >
                            <Eye size={14} />
                        </button>
                    </OverlayTrigger>

                    <OverlayTrigger placement="top" overlay={<Tooltip>Edit Expense</Tooltip>}>
                        <button
                            className="btn btn-outline-primary"
                            onClick={() => handleEdit(record)}
                        >
                            <Edit size={14} />
                        </button>
                    </OverlayTrigger>

                    <OverlayTrigger placement="top" overlay={<Tooltip>Delete Expense</Tooltip>}>
                        <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDelete(record)}
                        >
                            <Trash2 size={14} />
                        </button>
                    </OverlayTrigger>
                </div>
            ),
        },
    ], [getCategoryIcon, handleView, handleEdit, handleDelete]);

    // Render summary cards
    const renderSummaryCards = useCallback(() => {
        return (
            <div className="row mb-4">
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Expenses</p>
                                    <h3 className="mb-0 text-primary">
                                        {summary.total_expenses}
                                    </h3>
                                    <small className="text-muted">This period</small>
                                </div>
                                <div className="bg-primary bg-opacity-10 rounded-circle p-3">
                                    <FileText size={24} className="text-primary" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Amount</p>
                                    <h3 className="mb-0 text-danger">
                                        {expenseActions.formatCurrency(summary.total_amount)}
                                    </h3>
                                    <small className="text-muted">All expenses</small>
                                </div>
                                <div className="bg-danger bg-opacity-10 rounded-circle p-3">
                                    <DollarSign size={24} className="text-danger" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Average Amount</p>
                                    <h3 className="mb-0 text-success">
                                        {expenseActions.formatCurrency(summary.average_amount)}
                                    </h3>
                                    <small className="text-muted">Per expense</small>
                                </div>
                                <div className="bg-success bg-opacity-10 rounded-circle p-3">
                                    <TrendingUp size={24} className="text-success" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Categories</p>
                                    <h3 className="mb-0 text-info">
                                        {summary.total_categories || 0}
                                    </h3>
                                    <small className="text-muted">Active categories</small>
                                </div>
                                <div className="bg-info bg-opacity-10 rounded-circle p-3">
                                    <Folder size={24} className="text-info" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }, [summary]);

    // Render category summary cards
    const renderCategorySummary = useCallback(() => {
        if (!categorySummary || categorySummary.length === 0) return null;

        return (
            <div className="row mb-4">
                {(categorySummary || []).slice(0, 4).map((cat, idx) => (
                    <div className="col-xl-3 col-sm-6 col-12" key={cat.id || idx}>
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p className="mb-1 text-muted">{cat.category_name || 'Uncategorized'}</p>
                                        <h5 className="mb-0">
                                            {expenseActions.formatCurrency(cat.total_amount || 0)}
                                        </h5>
                                        <small className="text-muted">{cat.expense_count || 0} expenses</small>
                                    </div>
                                    <div
                                        className="rounded-circle p-3"
                                        style={{ backgroundColor: (cat.color || '#6c757d') + '20' }}
                                    >
                                        <span style={{ color: cat.color || '#6c757d' }}>
                                            {getCategoryIcon(cat.icon)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }, [categorySummary, getCategoryIcon]);

    // Tooltip renderer
    const renderTooltip = useCallback((props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    ), []);

    // Show loading spinner only on initial load
    if (initialLoad && loading) {
        return (
            <div className="page-wrapper">
                <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <h5 className="text-muted">Loading Expenses...</h5>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="add-item d-flex">
                        <div className="page-title">
                            <h4>Expenses</h4>
                            <h6>Manage your business expenses</h6>
                        </div>
                    </div>

                    <ul className="table-top-head">
                        <li>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleAdd}
                                disabled={loading}
                            >
                                <PlusCircle size={18} className="me-2" />
                                Add Expense
                            </button>
                        </li>

                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => loadExpenses(pagination.page)}
                                    disabled={loading}
                                >
                                    <RefreshCw size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <button
                                    className={data ? "btn btn-outline-secondary active" : "btn btn-outline-secondary"}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>

                {/* Error Message */}
                {apiError && (
                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        <strong>Error!</strong> {apiError}
                        <button type="button" className="btn-close" onClick={() => setApiError(null)}></button>
                    </div>
                )}

                {/* Summary Cards */}
                {renderSummaryCards()}

                {/* Category Summary */}
                {renderCategorySummary()}

                {/* Filters Card */}
                <div className="card table-list-card">
                    <div className="card-body">
                        <div className="table-top">
                            <div className="search-set">
                                <div className="search-input">
                                    <input
                                        type="text"
                                        placeholder="Search by expense no, paid to, description..."
                                        className="form-control form-control-sm formsearch"
                                        value={filters.search}
                                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                    />
                                    <button className="btn btn-searchset" onClick={handleSearch} disabled={loading}>
                                        <Search size={16} />
                                    </button>
                                </div>
                            </div>

                            <div className="search-path">
                                <div className="d-flex align-items-center">
                                    <button
                                        className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`}
                                        onClick={toggleFilterVisibility}
                                    >
                                        <Filter size={18} />
                                    </button>
                                </div>
                            </div>

                            <div className="form-sort">
                                <Sliders size={16} className="info-img me-2" />
                                <Select
                                    className="select"
                                    options={sortOptions}
                                    value={sortOptions.find(opt => opt.value === sortOption)}
                                    onChange={handleSortChange}
                                    placeholder="Sort by"
                                    isClearable={false}
                                />
                            </div>
                        </div>

                        {/* Filter Panel */}
                        <div
                            className={`card${isFilterVisible ? ' visible' : ''}`}
                            id="filter_inputs"
                            style={{ display: isFilterVisible ? 'block' : 'none' }}
                        >
                            <div className="card-body pb-0">
                                <div className="row">
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <label>Category</label>
                                            <Select
                                                options={categoryOptions}
                                                value={categoryOptions.find(opt => opt.value === filters.category_id)}
                                                onChange={(opt) => handleFilterChange('category_id', opt?.value || '')}
                                                placeholder="All Categories"
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <label>Payment Method</label>
                                            <Select
                                                options={paymentMethodOptions}
                                                value={paymentMethodOptions.find(m => m.value === filters.payment_method)}
                                                onChange={(opt) => handleFilterChange('payment_method', opt?.value || '')}
                                                placeholder="All Methods"
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <label>Status</label>
                                            <Select
                                                options={statusOptions}
                                                value={statusOptions.find(s => s.value === filters.status)}
                                                onChange={(opt) => handleFilterChange('status', opt?.value || '')}
                                                placeholder="All Status"
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-sm-6 col-12">
                                        <div className="input-blocks">
                                            <label>Date Range</label>
                                            <div className="d-flex gap-2">
                                                <DatePicker
                                                    selected={filters.from_date}
                                                    onChange={(date) => handleFilterChange('from_date', date)}
                                                    dateFormat="dd/MM/yyyy"
                                                    className="form-control"
                                                    placeholderText="From"
                                                    maxDate={filters.to_date || new Date()}
                                                    isClearable
                                                />
                                                <DatePicker
                                                    selected={filters.to_date}
                                                    onChange={(date) => handleFilterChange('to_date', date)}
                                                    dateFormat="dd/MM/yyyy"
                                                    className="form-control"
                                                    placeholderText="To"
                                                    minDate={filters.from_date}
                                                    maxDate={new Date()}
                                                    isClearable
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-lg-12 col-sm-12 col-12">
                                        <div className="input-blocks d-flex gap-2 mt-2 mb-3 justify-content-end">
                                            <button className="btn btn-primary" onClick={handleSearch} disabled={loading}>
                                                Apply Filters
                                            </button>
                                            <button className="btn btn-secondary" onClick={resetFilters} disabled={loading}>
                                                Reset
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Expenses Table */}
                        <div className="table-responsive">
                            <Table
                                columns={columns}
                                dataSource={expenses}
                                loading={loading}
                                pagination={{
                                    current: pagination.page,
                                    pageSize: pagination.limit,
                                    total: pagination.total,
                                    onChange: handlePageChange,
                                    showSizeChanger: true,
                                    showQuickJumper: true,
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} expenses`
                                }}
                                rowKey="id"
                            />
                        </div>

                        {!loading && expenses.length === 0 && !initialLoad && (
                            <div className="text-center py-4">
                                <p className="text-muted">No expenses found</p>
                                <button className="btn btn-primary btn-sm" onClick={resetFilters}>
                                    Clear Filters
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modals */}
            {modalType === 'add' && (
                <AddExpense
                    onSuccess={handleSuccess}
                    onClose={handleModalClose}
                    isOpen={true}
                    categories={categories || []}
                />
            )}

            {modalType === 'edit' && selectedExpense && (
                <EditExpense
                    expenseId={selectedExpense.id}
                    onSuccess={handleSuccess}
                    onClose={handleModalClose}
                    isOpen={true}
                    categories={categories || []}
                />
            )}

            {modalType === 'view' && selectedExpense && (
                <ViewExpense
                    expense={selectedExpense}
                    onClose={handleModalClose}
                />
            )}
        </div>
    );
};

export default ExpenseList;
